import { useMemo } from 'react';

import { Typography } from '@kc-console/common-ui';
import { getCustomFormatDate } from '@kc-console/core';
import { createColumnHelper, NameCell } from '@kc-console/shared-ui';

import EventStatusCell from './EventStatusCell';
import RelatedServicesCell from './RelatedServicesCell';

import type { EventItem } from '../model/types';

import { useRegionEndpoint } from '@/shared/hooks';

const DATE_FORMAT = 'yyyy.MM.dd (EEE) HH:mm';

const columnHelper = createColumnHelper<EventItem>();

/**
 * 이벤트 테이블 컬럼 정의 훅.
 * @description
 * - 제목(링크), 대상 서비스, 상태, 시작 일시, 종료 일시로 구성된다.
 * - 시작/종료 일시는 정렬 가능하다.
 */
const useEventColumns = () => {
  const { currentRegion } = useRegionEndpoint();
  return useMemo(
    () => [
      columnHelper.accessor('title', {
        id: 'title',
        header: '이벤트',
        cell: ({ row: { original } }) => (
          <NameCell
            name={original.title}
            href={`/events/${original.eventId}/info?region=${currentRegion}`}
          />
        ),
        enableHiding: false,
        size: 480,
      }),
      columnHelper.accessor('relatedServices', {
        id: 'relatedServices',
        header: '대상 서비스',
        cell: ({ row: { original } }) => (
          <RelatedServicesCell services={original.relatedServices ?? []} />
        ),
        enableSorting: false,
        size: 200,
      }),
      columnHelper.accessor('eventStatus', {
        id: 'eventStatus',
        header: '상태',
        cell: ({ row: { original } }) => <EventStatusCell status={original.eventStatus} />,
        enableSorting: true,
        size: 120,
        enableHiding: false,
      }),
      columnHelper.accessor('startDate', {
        id: 'startDate',
        header: '시작 일시',
        cell: ({ row: { original } }) => (
          <Typography variant="body/normal/md">
            {getCustomFormatDate(original.startDate, DATE_FORMAT)}
          </Typography>
        ),
        enableSorting: true,
        size: 200,
      }),
      columnHelper.accessor('endDate', {
        id: 'endDate',
        header: '종료 일시',
        cell: ({ row: { original } }) => (
          <Typography variant="body/normal/md">
            {original.endDate ? getCustomFormatDate(original.endDate, DATE_FORMAT) : '-'}
          </Typography>
        ),
        enableSorting: true,
        size: 200,
      }),
    ],
    [],
  );
};

export default useEventColumns;
